import type { AnalyticsEvent } from '$lib/services/tracking/googleAnalytics/events';
import type { Tracker } from '$lib/services/tracking/googleAnalytics/index';
import type { AnalyticsProperty } from '$lib/services/tracking/googleAnalytics/properties';
import type { TrackBasePayload } from '$lib/services/tracking/googleAnalytics/type';
import { browser, dev } from '$app/environment';
import { getBasePayload } from '$lib/services/tracking/googleAnalytics/basePayload';
import { GA_QUEUE_ID, GA_VENDOR_ID } from '$lib/services/tracking/googleAnalytics/constant';
import { useTrackingQueue } from '$lib/services/tracking/utils/trackingQueue';
import { isNogarnish } from '$lib/utils/nogarnish';

const { runEvent, passivePollingUntilReady } = useTrackingQueue<AnalyticsEvent>(GA_QUEUE_ID, GA_VENDOR_ID, false);

export function initializeGA({ afterSendEvent }: { afterSendEvent?: (event: AnalyticsEvent, payload: TrackBasePayload) => void } = {}): Tracker {
	if (!browser || isNogarnish() || !window) {
		return {
			track: () => {},
			setProperty: () => {},
		};
	}

	passivePollingUntilReady(() => Boolean(window.gtag));

	const sendEvent = (event: AnalyticsEvent) => {
		const payload = { ...getBasePayload(), ...(event as { payload?: any }).payload };

		if (dev) {
			// eslint-disable-next-line no-console
			console.log(`track:ga ${event.action}`, payload);
		}

		window.gtag?.('event', event.action, payload);

		if (typeof afterSendEvent === 'function') {
			afterSendEvent(event, payload);
		}
	};

	const setProperty = (property: AnalyticsProperty) => {
		if (dev) {
			// eslint-disable-next-line no-console
			console.log(`setProperty:ga ${property.name}`, property.payload);
		}

		if (window.gtag === undefined) {
			throw new Error('setProperty called but GTAG is not initialized');
		}

		window.gtag('set', property.name, property.payload);
	};

	return {
		track: (event: AnalyticsEvent) => runEvent(sendEvent, event),
		setProperty,
	};
}
